import { useState, useCallback, useEffect } from 'react';
import { useAuthContext } from '../../../context/AuthContext';
import useLanguage from '../../../hooks/useLanguage';
import { loadGoogleIdentityScript } from '../../../utils/googleIdentity';

const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

const useDesktopLoginLogic = ({ onLoginSuccess }) => {
  const [isLogin, setIsLogin] = useState(true);
  const setIsLoginProtected = useCallback(
    (newValue, reason = 'unknown') => {
      console.log(
        `[DESKTOP_LOGIN_STATE] Attempting to change isLogin from ${isLogin} to ${newValue}, reason: ${reason}`
      );
      setIsLogin(newValue);
    },
    [isLogin]
  );

  useEffect(() => {
    console.log('[DESKTOP_LOGIN_STATE] isLogin state changed to:', isLogin);
  }, [isLogin]);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    username: ''
  });

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [touchedFields, setTouchedFields] = useState({});
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [verificationInfo, setVerificationInfo] = useState(null);

  const isDevMode = process.env.REACT_APP_DEV_MODE === 'true';

  const { login: authLogin, loginWithGoogle, register, isLoading, error: authError, clearError, clearAuth } =
    useAuthContext();
  const { t } = useLanguage();
  const currentYear = new Date().getFullYear();

  const login = useCallback(
    (...args) => {
      if (!isLogin) {
        console.error('SECURITY VIOLATION: Login function called while in registration mode!');
        throw new Error('SECURITY: Login function called while in registration mode');
      }
      console.log('SECURITY: Login function call approved - currently in login mode');
      return authLogin(...args);
    },
    [authLogin, isLogin]
  );

  const calculatePasswordStrength = (password) => {
    let score = 0;
    if (password.length >= 8) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score++;
    return score;
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));

    if (name === 'password' && !isLogin) {
      setPasswordStrength(calculatePasswordStrength(value));
    }

    setTouchedFields((prev) => ({
      ...prev,
      [name]: true
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleBlur = (event) => {
    const { name } = event.target;
    setTouchedFields((prev) => ({
      ...prev,
      [name]: true
    }));
    validateField(name, formData[name]);
  };

  const validateField = (name, value) => {
    const trimmedValue = typeof value === 'string' ? value.trim() : value;
    let error = '';

    switch (name) {
      case 'email':
        if (!trimmedValue) {
          error = isLogin
            ? t('login.validation.emailOrUsernameRequired')
            : t('login.validation.emailRequired');
        } else if (isLogin) {
          if (trimmedValue.length < 3) {
            error = t('login.validation.loginIdentifierMin');
          }
        } else if (!EMAIL_REGEX.test(trimmedValue)) {
          error = t('login.validation.emailInvalid');
        }
        break;
      case 'password':
        if (!value) {
          error = t('login.validation.passwordRequired');
        } else if (value.length < 8) {
          error = t('login.validation.passwordLength');
        } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(value)) {
          error = t('login.validation.passwordComposition');
        }
        break;
      case 'confirmPassword':
        if (!isLogin) {
          if (!value) {
            error = t('login.validation.confirmPasswordRequired');
          } else if (value !== formData.password) {
            error = t('login.validation.passwordMismatch');
          }
        }
        break;
      case 'firstName':
        if (!isLogin && !trimmedValue) {
          error = t('login.validation.firstNameRequired');
        } else if (!isLogin && trimmedValue.length < 2) {
          error = t('login.validation.firstNameLength');
        }
        break;
      case 'lastName':
        if (!isLogin && !trimmedValue) {
          error = t('login.validation.lastNameRequired');
        } else if (!isLogin && trimmedValue.length < 2) {
          error = t('login.validation.lastNameLength');
        }
        break;
      case 'username':
        if (!isLogin && !trimmedValue) {
          error = t('login.validation.usernameRequired');
        } else if (!isLogin && trimmedValue.length < 3) {
          error = t('login.validation.usernameLength');
        } else if (!isLogin && !/^[a-zA-Z0-9_]+$/.test(trimmedValue)) {
          error = t('login.validation.usernameCharacters');
        }
        break;
      default:
        break;
    }

    setErrors((prev) => ({
      ...prev,
      [name]: error
    }));

    return error === '';
  };

  const validateForm = () => {
    const fieldsToValidate = ['email', 'password'];
    if (!isLogin) {
      fieldsToValidate.push('firstName', 'lastName', 'username', 'confirmPassword');
    }

    let isValid = true;
    fieldsToValidate.forEach((field) => {
      if (!validateField(field, formData[field])) {
        isValid = false;
      }
    });

    return isValid;
  };

  const handleDevLogin = async () => {
    if (!isDevMode) return;

    setIsProcessing(true);
    setErrors({});
    clearError();

    try {
      const devCredentials = {
        login: 'dev@loopino.com',
        password: 'dev-password',
        isDev: true
      };

      console.log('Attempting DEVELOPMENT LOGIN...');
      const response = await login(devCredentials);
      console.log('Development login successful:', response);

      if (response) {
        console.log('[DEV_LOGIN] Development login successful, proceeding with navigation');
        if (onLoginSuccess) {
          onLoginSuccess();
        }
      } else {
        throw new Error('Development login failed');
      }
    } catch (error) {
      console.error('Development login error:', error);
      setErrors({
        general: t('login.errors.devLoginFailed', {
          error: error?.message || '',
          defaultValue: 'Accesso DEV non riuscito: {{error}}'
        })
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleGoogleLogin = useCallback(async () => {
    if (isProcessing || isLoading) {
      return;
    }

    setErrors({});
    clearError();
    setVerificationInfo(null);
    setIsProcessing(true);

    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

    if (!clientId) {
      setErrors({ general: t('login.errors.googleMissingClientId') });
      setIsProcessing(false);
      return;
    }

    try {
      await loadGoogleIdentityScript();

      if (!window.google?.accounts?.id) {
        throw new Error(t('login.errors.googleUnavailable'));
      }

      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: async (response) => {
          if (!response?.credential) {
            setErrors({ general: t('login.errors.googleFailed') });
            setIsProcessing(false);
            return;
          }

          try {
            const authResponse = await loginWithGoogle(response.credential);

            if (authResponse?.user?.is_verified === false) {
              const pendingEmail = authResponse.user?.email;
              clearAuth();

              const verificationMessage = t('login.messages.unverified', { email: pendingEmail });
              setErrors({ general: verificationMessage });
              setVerificationInfo({
                type: 'unverified',
                email: pendingEmail,
                token: null
              });
              return;
            }

            if (onLoginSuccess) {
              onLoginSuccess();
            }
          } catch (error) {
            setErrors({ general: error.message || t('login.errors.googleFailed') });
          } finally {
            setIsProcessing(false);
          }
        }
      });

      window.google.accounts.id.prompt((notification) => {
        if (notification.isNotDisplayed?.() || notification.isSkippedMoment?.()) {
          setErrors({ general: t('login.errors.googlePromptBlocked') });
          setIsProcessing(false);
        } else if (notification.isDismissedMoment?.()) {
          setIsProcessing(false);
        }
      });
    } catch (error) {
      setErrors({ general: error.message || t('login.errors.googleFailed') });
      setIsProcessing(false);
    }
  }, [clearAuth, clearError, isLoading, isProcessing, loginWithGoogle, onLoginSuccess, t]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    if (isProcessing) {
      console.log('Form submission blocked - already processing');
      return;
    }

    setErrors({});
    clearError();
    setVerificationInfo(null);
    setIsProcessing(true);

    console.log('Form submitted in mode:', isLogin ? 'LOGIN' : 'REGISTRATION');

    try {
      if (isLogin) {
        if (!isLogin) {
          throw new Error('SECURITY: Login attempted while not in login mode');
        }

        const loginIdentifier = formData.email.trim();
        const credentials = {
          login: loginIdentifier,
          password: formData.password
        };

        console.log('Attempting LOGIN with identifier:', loginIdentifier);
        const response = await login(credentials);
        console.log('Login successful:', response);

        if (response) {
          if (response.user?.is_verified === false) {
            const pendingEmail = response.user?.email || loginIdentifier;
            console.warn('[DESKTOP_LOGIN_STATE] Login blocked - user not verified', {
              pendingEmail,
              userId: response.user?.id
            });

            clearAuth();

            const verificationMessage = t('login.messages.unverified', { email: pendingEmail });
            setErrors({
              general: verificationMessage
            });
            setVerificationInfo({
              type: 'unverified',
              email: pendingEmail,
              token: null
            });
            return;
          }

          console.log('[DESKTOP_LOGIN_STATE] Login truly successful, proceeding with navigation');
          setVerificationInfo(null);
          if (onLoginSuccess) {
            onLoginSuccess();
          }
        } else {
          console.error('[DESKTOP_LOGIN_STATE] Login response missing access token:', response);
          throw new Error('Login response missing required authentication data');
        }
      } else {
        if (isLogin) {
          throw new Error('SECURITY: Registration attempted while in login mode');
        }

        const trimmedEmail = formData.email.trim();
        const trimmedUsername = formData.username.trim();
        const trimmedFirstName = formData.firstName.trim();
        const trimmedLastName = formData.lastName.trim();

        const userData = {
          email: trimmedEmail,
          username: trimmedUsername,
          password: formData.password,
          first_name: trimmedFirstName,
          last_name: trimmedLastName
        };

        console.log('Attempting REGISTRATION with email:', trimmedEmail);
        const response = await register(userData);

        const isSuccess =
          response &&
          (response.user ||
            response._status === 200 ||
            response._status === 201 ||
            response.message?.includes('success'));

        if (isSuccess) {
          console.log('Registration successful:', response);
          setIsLoginProtected(true, 'registration success');
          setFormData({
            email: trimmedEmail,
            password: '',
            confirmPassword: '',
            firstName: '',
            lastName: '',
            username: ''
          });
          setPasswordStrength(0);

          const successMessage = response.message || t('login.messages.registrationSuccess');
          setErrors({
            general: successMessage
          });

          setVerificationInfo({
            type: 'registration',
            email: trimmedEmail,
            token: isDevMode && response.verification_token ? response.verification_token : null
          });

          console.log('Registration completed successfully, switched to login mode');
        } else {
          console.log('Unexpected response format:', response);
          throw new Error(t('login.errors.registrationGeneric', { defaultValue: 'Errore durante la registrazione. Riprova.' }));
        }
      }
    } catch (error) {
      console.error('Error:', error);

      setVerificationInfo(null);

      const rawMessage =
        error.message || (isLogin ? t('login.errors.loginInvalid') : t('login.errors.registrationGeneric'));
      const normalizedMessage = rawMessage.toLowerCase();

      let errorMessage = rawMessage;

      if (
        normalizedMessage.includes('rate limit') ||
        normalizedMessage.includes('too many') ||
        normalizedMessage.includes('429')
      ) {
        errorMessage = t('login.errors.rateLimited');
      }

      if (!isLogin) {
        if (
          normalizedMessage.includes('email already exists') ||
          normalizedMessage.includes('user with this email')
        ) {
          errorMessage = t('login.errors.emailExists');
        } else if (normalizedMessage.includes('username already exists')) {
          errorMessage = t('login.errors.usernameExists');
        }

        console.log('Registration error - ensuring we stay in registration mode');
        console.log('SECURITY: Blocking any potential login attempts after registration failure');
      }

      const fieldErrors = {};

      if (isLogin) {
        if (normalizedMessage.includes('credential') || normalizedMessage.includes('login')) {
          fieldErrors.email = t('login.errors.fieldEmailInvalid');
        }
        if (normalizedMessage.includes('password')) {
          fieldErrors.password = t('login.errors.fieldPasswordInvalid');
        }
      } else {
        if (normalizedMessage.includes('email')) {
          fieldErrors.email = t('login.errors.fieldEmailHint');
        }
        if (normalizedMessage.includes('username')) {
          fieldErrors.username = t('login.errors.fieldUsernameInvalid');
        }
        if (normalizedMessage.includes('password')) {
          fieldErrors.password = t('login.errors.fieldPasswordInvalid');
        }
      }

      console.log('Setting error message:', errorMessage);
      setErrors({
        ...fieldErrors,
        general: errorMessage
      });

      console.log('Error occurred, staying on current form mode:', isLogin ? 'login' : 'registration');
    } finally {
      setIsProcessing(false);
    }
  };

  const clearErrorsAndToggle = () => {
    setErrors({});
    clearError();
    setVerificationInfo(null);
    setPasswordStrength(0);
    setTouchedFields({});
    setIsLoginProtected(!isLogin, 'clearErrorsAndToggle');
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
      username: ''
    });
  };

  const toggleMode = () => {
    if (isProcessing) {
      console.log('Blocking mode switch - currently processing');
      return;
    }

    if (errors.general) {
      console.log('Cannot switch modes with active errors. User needs to clear them first.');
      return;
    }

    console.log(
      'Mode switch allowed - switching from',
      isLogin ? 'login' : 'register',
      'to',
      !isLogin ? 'login' : 'register'
    );
    setIsLoginProtected(!isLogin, 'toggleMode');
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
      username: ''
    });
    setErrors({});
    clearError();
    setVerificationInfo(null);
    setPasswordStrength(0);
    setTouchedFields({});
  };

  const clearGeneralMessage = useCallback(() => {
    setErrors((prev) => ({ ...prev, general: '' }));
    clearError();
    setVerificationInfo(null);
  }, [clearError]);

  const generalMessage = errors.general || authError;
  const isGeneralSuccess =
    !!generalMessage && (generalMessage.includes('✅') || verificationInfo?.type === 'registration');
  const formTitle = isLogin ? t('login.title.login') : t('login.title.register');
  const formDescription = isLogin ? t('login.description.login') : t('login.description.register');
  const emailLabel = isLogin ? t('login.fields.emailLogin') : t('login.fields.emailRegister');
  const submitLabel = isLogin ? t('login.buttons.submitLogin') : t('login.buttons.submitRegister');
  const togglePrompt = isLogin ? t('login.toggle.promptLogin') : t('login.toggle.promptRegister');
  const toggleAction = isLogin ? t('login.toggle.register') : t('login.toggle.login');
  const toggleTitle = errors.general ? t('login.toggle.clearErrorHint') : undefined;
  const passwordStrengthLabel = (() => {
    if (passwordStrength <= 2) return t('login.passwordStrength.weak');
    if (passwordStrength === 3) return t('login.passwordStrength.good');
    if (passwordStrength === 4) return t('login.passwordStrength.strong');
    if (passwordStrength === 5) return t('login.passwordStrength.veryStrong');
    return '';
  })();
  const googleLabel = t('login.buttons.google');
  const dividerLabel = t('login.divider');
  const rememberMeLabel = t('login.buttons.rememberMe');
  const forgotPasswordLabel = t('login.buttons.forgotPassword');
  const clearMessageTitle = t('login.buttons.clearMessage');
  const footerCopy = t('login.footer.copy', { year: currentYear });
  const footerLinks = {
    privacy: t('login.footer.privacy'),
    terms: t('login.footer.terms'),
    cookies: t('login.footer.cookies'),
    legal: t('login.footer.legal'),
    help: t('login.footer.help')
  };

  return {
    t,
    isLogin,
    formData,
    errors,
    showPassword,
    setShowPassword,
    showConfirmPassword,
    setShowConfirmPassword,
    touchedFields,
    passwordStrength,
    isProcessing,
    verificationInfo,
    isDevMode,
    isLoading,
    generalMessage,
    isGeneralSuccess,
    formTitle,
    formDescription,
    emailLabel,
    submitLabel,
    togglePrompt,
    toggleAction,
    toggleTitle,
    passwordStrengthLabel,
    googleLabel,
    dividerLabel,
    rememberMeLabel,
    forgotPasswordLabel,
    clearMessageTitle,
    footerCopy,
    footerLinks,
    handleInputChange,
    handleBlur,
    handleSubmit,
    handleDevLogin,
    handleGoogleLogin,
    clearErrorsAndToggle,
    toggleMode,
    clearGeneralMessage
  };
};

export default useDesktopLoginLogic;
